import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Checkbox,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
  Chip,
  ListSubheader,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastSuccess from "../../errors/toastSuccess";

const ForwardMessageModal = ({ open, onClose, selectedMessages }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [contacts, setContacts] = useState([]);
  // "Conversas recentes" (só com a busca vazia), como no WhatsApp
  const [recentContacts, setRecentContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const requestRef = useRef(0);

  // Busca NO SERVIDOR (nome, número ou e-mail, em todos os contatos). Antes a
  // janela carregava só os 20 primeiros em ordem alfabética e filtrava entre eles
  // — quem estava fora desses 20 não aparecia na busca.
  useEffect(() => {
    if (!open) return undefined;
    setPageNumber(1);
    const timer = setTimeout(() => fetchContacts(searchTerm, 1), 350);
    return () => clearTimeout(timer);
  }, [open, searchTerm]);

  const fetchContacts = async (term, page) => {
    const reqId = ++requestRef.current;
    const busca = term.trim();
    setLoading(true);
    try {
      // Sem busca: 10 conversas recentes do atendente + todos em ordem alfabética.
      // Com busca: lista única com o resultado.
      const [{ data }, recentes] = await Promise.all([
        api.get("/contacts", {
          params: { searchParam: busca, pageNumber: page, orderBy: busca ? "recent" : "alpha" }
        }),
        !busca && page === 1
          ? api.get("/contacts", { params: { searchParam: "", pageNumber: 1, orderBy: "recent" } })
          : Promise.resolve(null)
      ]);
      // descarta resposta atrasada de uma busca anterior
      if (reqId !== requestRef.current) return;
      if (page === 1) setRecentContacts(recentes ? recentes.data.contacts.slice(0, 10) : []);
      setContacts(prev => (page === 1 ? data.contacts : [...prev, ...data.contacts]));
      setHasMore(!!data.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  };

  // Rolagem até o fim da lista carrega a próxima página.
  const handleScroll = e => {
    const el = e.currentTarget;
    if (loading || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      const next = pageNumber + 1;
      setPageNumber(next);
      fetchContacts(searchTerm, next);
    }
  };

  const renderContact = (contact, secao) => (
    <ListItem
      key={`${secao}-${contact.id}`}
      button
      onClick={() => handleContactToggle(contact)}
      dense
    >
      <Checkbox
        checked={selectedContacts.some(c => c.id === contact.id)}
        tabIndex={-1}
        disableRipple
      />
      <ListItemAvatar>
        <Avatar src={contact.profilePicUrl}>
          {(contact.name || "?").charAt(0).toUpperCase()}
        </Avatar>
      </ListItemAvatar>
      <ListItemText primary={contact.name} secondary={contact.number} />
    </ListItem>
  );

  const SectionTitle = ({ children }) => (
    <ListSubheader disableSticky sx={{ lineHeight: "32px", fontWeight: 600, bgcolor: "transparent" }}>
      {children}
    </ListSubheader>
  );

  const handleContactToggle = (contact) => {
    const isSelected = selectedContacts.some(c => c.id === contact.id);
    if (isSelected) {
      setSelectedContacts(selectedContacts.filter(c => c.id !== contact.id));
    } else {
      setSelectedContacts([...selectedContacts, contact]);
    }
  };

  const handleForwardMessages = async () => {
    if (selectedContacts.length === 0) {
      return;
    }

    setSending(true);
    try {
      const promises = selectedContacts.map(async (contact) => {
        try {
          const ticketId = contact.ticketId || 0;
          
          return api.post(`/messages/${ticketId}/forward`, {
            contactId: contact.id,
            // conversa de origem: o encaminhamento sai pela mesma conta dela
            originTicketId: selectedMessages[0]?.ticketId,
            messages: selectedMessages.map(msg => ({
              id: msg.id,
              body: msg.body,
              mediaType: msg.mediaType,
              mediaUrl: msg.mediaUrl,
              quotedMsg: msg.quotedMsg,
            }))
          });
        } catch (contactError) {
          console.error(`Erro ao processar contato ${contact.name}:`, contactError);
          throw contactError;
        }
      });

      await Promise.all(promises);
      toastSuccess(t("forwardMessages.forwardedSuccess"));
      onClose();
      setSelectedContacts([]);
    } catch (err) {
      console.error("Erro ao encaminhar mensagens:", err);
      toastError(err, t);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setSearchTerm("");
    setSelectedContacts([]);
    setContacts([]);
    setRecentContacts([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          height: "80vh",
          maxHeight: "600px",
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {t("forwardMessages.title")}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t("forwardMessages.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
          autoFocus
        />

        {/* Escolhidos ficam visíveis mesmo trocando a busca */}
        {selectedContacts.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
            {selectedContacts.map(c => (
              <Chip
                key={c.id}
                size="small"
                label={c.name}
                onDelete={() => handleContactToggle(c)}
              />
            ))}
          </Box>
        )}

        {loading && contacts.length === 0 ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : !loading && contacts.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: "center" }}>
            Nenhum contato encontrado.
          </Typography>
        ) : (
          <List sx={{ maxHeight: "360px", overflow: "auto" }} onScroll={handleScroll}>
            {recentContacts.length > 0 && !searchTerm.trim() && (
              <>
                <SectionTitle>Conversas recentes</SectionTitle>
                {recentContacts.map(c => renderContact(c, "recente"))}
                <SectionTitle>Todos os contatos</SectionTitle>
              </>
            )}
            {contacts.map(c => renderContact(c, "todos"))}
            {loading && (
              <Box display="flex" justifyContent="center" p={1}>
                <CircularProgress size={20} />
              </Box>
            )}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Typography variant="body2" color="textSecondary">
            {selectedMessages.length} {selectedMessages.length === 1 
              ? t("forwardMessages.selectedCount") 
              : t("forwardMessages.selectedCountPlural")
            }
          </Typography>
          
          <Box>
            <Button onClick={handleClose} sx={{ mr: 1 }}>
              {t("forwardMessages.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleForwardMessages}
              disabled={selectedContacts.length === 0 || sending}
              startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
            >
              {t("forwardMessages.forwardButton")}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ForwardMessageModal;
